import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  BREAKPOINTS_WIDTHS,
  formatMoney,
  template,
  useWindowSize,
} from '@belong/common';
import { Button, Text } from '@belong/ui';
import PaymentsModal from 'containercomponents/Modals/PaymentsModal/PaymentsModal';
import { PaymentPreference, PaymentPriority } from 'models/enums';
import { QuestionsOrConcernsModalFooter } from 'post-inspection/components/question-or-concern-modal-footer/question-or-concern-modal-footer';
import { updatePaymentMethod } from 'store/redux/user/actions';
import { PaygPaymentMethodRow } from '../components/payg-payment-method-row';
import {
  PaymentMethodTypeTiles,
  type PaymentMethodTypeKey,
} from '../components/payment-method-type-tiles';
import { PAY_AS_YOU_GO_STRINGS } from '../strings';
import { usePaygAmounts } from '../use-payg-amounts';
import { usePaygPaymentMethod } from '../use-payg-payment-method';

const STRINGS = PAY_AS_YOU_GO_STRINGS.methodStep;

type Props = {
  onDone: () => void;
};

export function PaygMethodStep({ onDone }: Props) {
  const dispatch = useDispatch();
  const { upfront, remaining } = usePaygAmounts();
  const paymentMethod = usePaygPaymentMethod();
  const { width } = useWindowSize();

  const isMobile = width <= BREAKPOINTS_WIDTHS.SM;

  const [addingType, setAddingType] = useState<PaymentMethodTypeKey | null>(
    null,
  );

  return (
    <div className="flex w-full flex-col items-center pt-2sm sm:pt-2xl">
      <div className="flex flex-col items-center gap-xs text-center sm:max-w-[340px] md:max-w-[700px]">
        <Text fontWeight="semibold" variant="h2">
          {template(STRINGS.title, {
            upfront: formatMoney(upfront, 'DOLLARS_WITH_CENTS'),
          })}
        </Text>
        <Text variant={isMobile ? 'p1' : 'body'}>
          {template(STRINGS.subtitle, {
            remaining: formatMoney(remaining, 'DOLLARS_WITH_CENTS'),
          })}
        </Text>
      </div>

      <div className="mt-xl flex w-full flex-col gap-sm sm:mt-2xl md:px-xl">
        {paymentMethod ? (
          <PaygPaymentMethodRow paymentMethod={paymentMethod} />
        ) : (
          <PaymentMethodTypeTiles onSelect={setAddingType} />
        )}
        <div className="flex flex-col gap-2xs">
          <Text className="text-dark-gray" variant="p1">
            {STRINGS.cardFeeNote}
          </Text>
          <Text className="text-dark-gray" variant="p1">
            {STRINGS.internationalFeeNote}
          </Text>
        </div>
      </div>

      <div className="mt-xl w-full pb-xl sm:mt-2xl md:pb-2xl">
        <QuestionsOrConcernsModalFooter bareOnDesktop>
          <Button
            className="md:w-[260px]"
            disabled={!paymentMethod}
            onClick={onDone}
            size={{ _: 'fluid' }}
          >
            {STRINGS.cta}
          </Button>
        </QuestionsOrConcernsModalFooter>
      </div>

      {addingType && (
        <PaymentsModal
          backdrop="static"
          forcePaymentMethod={addingType}
          keyboard={false}
          onHide={() => setAddingType(null)}
          onPaymentSelection={async (accountId: string) => {
            await dispatch(
              updatePaymentMethod(
                accountId,
                PaymentPreference.Maintenance,
                PaymentPriority.Primary,
              ),
            );
            setAddingType(null);
            onDone();
          }}
          show
        />
      )}
    </div>
  );
}
